/**
 * Navigation and breadcrumb behavior, independently deployable from the global theme bundle.
 */
(function navigationBehavior(Drupal, once) {
  const desktopBreakpoint = 1200;
  const tabletBreakpoint = 768;

  Drupal.behaviors.curaparcClinicNavigation = {
    attach(context) {
      once("curaparc-clinic-navigation", "[data-site-navigation]", context).forEach((header) => {
        const panel = header.querySelector("#site-navigation-panel");
        const navigation = header.querySelector(".site-header__navigation");
        const rootMenu = navigation?.querySelector(".site-menu--level-0");
        const toggle = header.querySelector("[data-site-navigation-toggle]");
        const cta = header.querySelector("[data-site-navigation-cta]");
        const ctaSlot = header.querySelector("[data-site-navigation-cta-slot]");
        const closeButtons = header.querySelectorAll("[data-site-navigation-close]");
        const branchItems = header.querySelectorAll(".site-menu__item--has-children");
        const mainContent = document.querySelector("main");
        const supportsHover = window.matchMedia("(hover: hover) and (pointer: fine)");
        const hoverCloseTimers = new WeakMap();
        const hoverLeaveLocks = new WeakSet();
        let previouslyFocused = null;
        let resizeFrame = null;

        if (!panel || !toggle || !rootMenu) {
          return;
        }

        const isCompact = () => (
          window.innerWidth < desktopBreakpoint || header.classList.contains("site-header--compact")
        );

        const syncHeaderOffset = () => {
          if (mainContent) {
            mainContent.style.paddingTop = `${header.offsetHeight}px`;
          }
        };

        const updateStickyHeader = () => {
          header.classList.toggle("site-header--scrolled", window.scrollY > 0);
        };

        const syncCtaPlacement = () => {
          if (!cta || !ctaSlot) {
            return;
          }

          if (window.innerWidth < tabletBreakpoint) {
            ctaSlot.append(cta);
          } else {
            toggle.before(cta);
          }
        };

        const setBranchState = (item, open) => {
          const branchToggle = item.querySelector(":scope > .site-menu__row > [data-menu-toggle]");
          item.classList.toggle("is-open", open);
          branchToggle?.setAttribute("aria-expanded", String(open));

          if (!open) {
            if (item.parentElement === rootMenu) {
              hoverLeaveLocks.delete(item);
            }

            item.querySelectorAll(".site-menu__item--has-children.is-open").forEach((descendant) => {
              descendant.classList.remove("is-open");
              descendant.querySelector(":scope > .site-menu__row > [data-menu-toggle]")?.setAttribute("aria-expanded", "false");
            });
          }
        };

        const clearHoverClose = (item) => {
          const timer = hoverCloseTimers.get(item);
          if (timer) {
            window.clearTimeout(timer);
            hoverCloseTimers.delete(item);
          }
        };

        const scheduleHoverClose = (item) => {
          clearHoverClose(item);
          hoverCloseTimers.set(item, window.setTimeout(() => {
            hoverCloseTimers.delete(item);
            if (!item.matches(":hover")) {
              setBranchState(item, false);
            }
          }, 180));
        };

        const closeSiblingBranches = (item) => {
          const list = item.parentElement;
          if (!list) {
            return;
          }

          Array.from(list.children).forEach((sibling) => {
            if (sibling !== item && sibling.classList.contains("site-menu__item--has-children")) {
              setBranchState(sibling, false);
            }
          });
        };

        const lockDropdownForPointerShift = (item) => {
          if (isCompact() || item.parentElement === rootMenu) {
            return;
          }

          const hasOpenSibling = Array.from(item.parentElement?.children || []).some((sibling) => (
            sibling !== item && sibling.classList.contains("is-open")
          ));
          const topLevelItem = item.closest(".site-menu--level-0 > .site-menu__item");

          if (!hasOpenSibling || !topLevelItem) {
            return;
          }

          hoverLeaveLocks.add(topLevelItem);
        };

        const closeAllBranches = () => {
          rootMenu.querySelectorAll(".site-menu__item--has-children.is-open").forEach((item) => {
            setBranchState(item, false);
          });
        };

        const positionDropdown = (item) => {
          if (isCompact() || item.parentElement !== rootMenu) {
            return;
          }

          const branch = item.querySelector(":scope > .site-menu__branch");
          if (!branch) {
            return;
          }

          branch.style.setProperty("--menu-shift", "0px");
          const bounds = branch.getBoundingClientRect();
          const edge = 16;
          let shift = 0;

          if (bounds.left < edge) {
            shift = edge - bounds.left;
          } else if (bounds.right > window.innerWidth - edge) {
            shift = window.innerWidth - edge - bounds.right;
          }

          branch.style.setProperty("--menu-shift", `${shift}px`);
        };

        const openNavigation = () => {
          if (!isCompact()) {
            return;
          }

          previouslyFocused = document.activeElement;
          header.classList.add("site-header--menu-open");
          document.body.classList.add("site-navigation-is-open");
          toggle.setAttribute("aria-expanded", "true");
          panel.setAttribute("aria-hidden", "false");
          header.querySelector("[data-site-navigation-close]")?.focus();
        };

        const closeNavigation = (restoreFocus = true) => {
          header.classList.remove("site-header--menu-open");
          document.body.classList.remove("site-navigation-is-open");
          toggle.setAttribute("aria-expanded", "false");
          panel.setAttribute("aria-hidden", String(isCompact()));
          closeAllBranches();

          if (restoreFocus && previouslyFocused instanceof HTMLElement) {
            previouslyFocused.focus();
          }
        };

        const measureNavigation = () => {
          syncCtaPlacement();
          header.classList.remove("site-header--compact");

          if (window.innerWidth >= desktopBreakpoint) {
            const branding = header.querySelector(".site-header__branding");
            const menuItemContents = Array.from(rootMenu.querySelectorAll(":scope > .site-menu__item")).map((item) => (
              item.querySelector(":scope > .site-menu__row, :scope > .site-menu__link")
            )).filter(Boolean);
            const navigationBounds = navigation.getBoundingClientRect();
            const brandingBounds = branding?.getBoundingClientRect();
            const itemBounds = menuItemContents.map((item) => item.getBoundingClientRect());
            const firstItemBounds = itemBounds[0];
            const minimumBrandingGap = 48;
            const minimumItemGap = 4;
            const navigationStart = brandingBounds?.right ?? navigationBounds.left;
            const keepsBrandingClear = !firstItemBounds
              || firstItemBounds.left >= navigationStart + minimumBrandingGap;
            const keepsItemsSeparate = itemBounds.every((bounds, index) => (
              index === itemBounds.length - 1
              || bounds.right + minimumItemGap <= itemBounds[index + 1].left
            ));

            header.classList.toggle(
              "site-header--compact",
              !keepsBrandingClear || !keepsItemsSeparate,
            );
          }

          if (!isCompact()) {
            closeNavigation(false);
            panel.setAttribute("aria-hidden", "false");
          } else if (!header.classList.contains("site-header--menu-open")) {
            panel.setAttribute("aria-hidden", "true");
          }

          syncHeaderOffset();
        };

        toggle.addEventListener("click", openNavigation);
        closeButtons.forEach((button) => button.addEventListener("click", () => closeNavigation()));

        branchItems.forEach((item) => {
          const branchToggle = item.querySelector(":scope > .site-menu__row > [data-menu-toggle]");
          if (!branchToggle) {
            return;
          }

          branchToggle.addEventListener("click", () => {
            const willOpen = !item.classList.contains("is-open");
            if (willOpen) {
              lockDropdownForPointerShift(item);
            }
            closeSiblingBranches(item);
            setBranchState(item, willOpen);
            if (willOpen) {
              positionDropdown(item);
            }
          });
        });

        rootMenu.querySelectorAll(":scope > .site-menu__item--has-children").forEach((item) => {
          item.addEventListener("pointerenter", () => {
            if (isCompact() || !supportsHover.matches) {
              return;
            }

            hoverLeaveLocks.delete(item);
            clearHoverClose(item);
            closeSiblingBranches(item);
            setBranchState(item, true);
            positionDropdown(item);
          });

          item.addEventListener("pointerleave", () => {
            if (isCompact() || !supportsHover.matches) {
              return;
            }

            if (hoverLeaveLocks.has(item)) {
              return;
            }

            scheduleHoverClose(item);
          });
        });

        document.addEventListener("pointerdown", (event) => {
          if (!isCompact() && event.target instanceof Node && !header.contains(event.target)) {
            closeAllBranches();
          }
        });

        navigation.addEventListener("click", (event) => {
          if (isCompact() && event.target.closest("a")) {
            closeNavigation(false);
          }
        });

        header.addEventListener("keydown", (event) => {
          if (event.key === "Escape") {
            if (header.classList.contains("site-header--menu-open")) {
              closeNavigation();
            } else {
              closeAllBranches();
              if (isCompact()) {
                toggle.focus();
              }
            }
          }

          if (event.key === "Tab" && header.classList.contains("site-header--menu-open")) {
            const focusableElements = Array.from(header.querySelectorAll(
              'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
            )).filter((element) => element !== toggle && element.getClientRects().length > 0);
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstElement) {
              event.preventDefault();
              lastElement?.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
              event.preventDefault();
              firstElement?.focus();
            }
          }
        });

        window.addEventListener("resize", () => {
          window.cancelAnimationFrame(resizeFrame);
          resizeFrame = window.requestAnimationFrame(measureNavigation);
        });

        window.addEventListener("scroll", updateStickyHeader, { passive: true });

        measureNavigation();
        updateStickyHeader();
        document.fonts?.ready.then(measureNavigation);
      });
    },
  };
})(Drupal, once);

(function breadcrumbBehavior(Drupal, once) {
  Drupal.behaviors.curaparcClinicBreadcrumb = {
    attach(context) {
      once("curaparc-clinic-breadcrumb-position", "[data-breadcrumb-navigation]", context).forEach((breadcrumb) => {
        const content = document.querySelector("#main-content");
        const breadcrumbBlock = breadcrumb.closest(".block-system-breadcrumb-block") || breadcrumb;
        const originalHeader = breadcrumbBlock.closest(".page__header");

        if (!content) {
          return;
        }

        const hero = content.querySelector([
          ".paragraph--type--header-verteilerseiten-mit-cta",
          ".paragraph--type--slideshow",
        ].join(", "));
        const heroItem = hero?.closest(".field--name-field-content > .field__item");
        const firstContentItem = content.querySelector(".field--name-field-content > .field__item");
        const insertionTarget = heroItem || firstContentItem;

        if (!insertionTarget) {
          return;
        }

        insertionTarget.insertAdjacentElement("afterend", breadcrumbBlock);

        if (originalHeader && !originalHeader.querySelector(".block")) {
          originalHeader.remove();
        }
      });
    },
  };
})(Drupal, once);
